import { FormError, Select } from "@msuite/ui/form";
import { useLoading } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { type MergeProjectSchema, mergeProjectSchema } from "@msuite/utils/schemas";
import type { Nullable } from "@msuite/utils/types";
import React from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import { ProjectSearch } from "@/components/projects";
import { client } from "@/hono";
import { MergePreview } from "./merge-preview";
import { MergeProjectConfirmModal } from "./merge-project-confirm-modal";
import { MERGE_CONFIRM_VALUE } from "./shared";

/** Props Interface */
interface MergeProjectModalProps extends ModalProps {
	projectId: string;
	onComplete: () => void;
}

export function MergeProjectModal({ projectId, isOpen, onClose }: MergeProjectModalProps) {
	/** Hooks */
	const qc = useQueryClient();
	const mergingLoader = useLoading();
	const controller = useAsyncModal<string>();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const methods = useForm({
		resolver: resolver(mergeProjectSchema),
		defaultValues: {
			sourceProjectId: projectId,
			direction: "source_to_target",
		},
		mode: "onChange",
	});
	const currentTargetProjectId = useWatch({ control: methods.control, name: "targetProjectId" });

	/** Functions */
	function handleOnChangeTargetId(targetId: Nullable<string>) {
		try {
			methods.setValue("targetProjectId", targetId || "");
		} catch {
			toast.error(t("general.something_went_wrong"));
		}
	}

	async function handleOnSubmit(json: MergeProjectSchema) {
		const confirmValue = await controller.confirm();
		if (confirmValue !== MERGE_CONFIRM_VALUE) return;
		toast.promise(
			async () => {
				mergingLoader.start();
				try {
					const response = await client.projects[":projectId"].settings.merge.$patch({
						param: { projectId },
						json,
					});
					await responseHandler(response);
				} finally {
					mergingLoader.stop();
				}
				await qc.invalidateQueries({ predicate: invalidate(["projects"]) });
				onClose();
				const targetId = json.direction === "source_to_target" ? json.targetProjectId : projectId;
				navigate(`/projects/${targetId}`);
			},
			{
				loading: t("projects.merging_projects"),
			},
		);
	}

	/** Render */
	return (
		<React.Fragment>
			<FormProvider {...methods}>
				<Modal
					isOpen={isOpen}
					onClose={onClose}
					title={t("projects.merge_project")}
					onSubmit={methods.handleSubmit(handleOnSubmit)}
					data-cy="merge-project-modal"
				>
					<Modal.Body>
						<ProjectSearch onChange={handleOnChangeTargetId} />
						<FormError
							errors={methods.formState.errors}
							name="targetProjectId"
							customMessage={t("projects.merge_project_target_cannot_be_same_as_source")}
						/>
						<Select
							label={t("projects.merge_project_direction")}
							control={methods.control}
							name="direction"
							isRequired
							isNullable={false}
							itemNotFoundLabel="No direction found"
							options={[
								{
									label: t("projects.merge_project_direction_source_to_target"),
									value: "source_to_target",
									item: {
										direction: "source_to_target",
									},
								},
								{
									label: t("projects.merge_project_direction_target_to_source"),
									value: "target_to_source",
									item: {
										direction: "target_to_source",
									},
								},
							]}
						/>
						<MergePreview
							sourceProjectId={projectId}
							targetProjectId={currentTargetProjectId}
						/>
					</Modal.Body>
					<Modal.FooterForm isLoading={mergingLoader.isLoading} />
				</Modal>
			</FormProvider>
			<MergeProjectConfirmModal controller={controller} />
		</React.Fragment>
	);
}
